
import { useState, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PackingListEditor } from './PackingListEditor';

interface PackingListSelectorProps {
  blogPostId: string;
  selectedPackingListId?: string;
  onSelect: (packingListId: string) => void;
}

export const PackingListSelector = ({
  blogPostId,
  selectedPackingListId,
  onSelect
}: PackingListSelectorProps) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Fetch all packing lists
  const { data: packingLists, isLoading, refetch } = useQuery({
    queryKey: ['packingLists'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('packing_lists')
        .select('*')
        .order('title');
        
      if (error) throw error;
      return data || [];
    }
  });
  
  // Refetch when dialog closes
  useEffect(() => {
    if (!createDialogOpen) {
      refetch();
    }
  }, [createDialogOpen, refetch]);
  
  const handleCreateNewList = () => {
    setCreateDialogOpen(true);
  };
  
  const handleListCreated = (newPackingListId: string) => {
    setCreateDialogOpen(false);
    onSelect(newPackingListId);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <Select 
            value={selectedPackingListId} 
            onValueChange={onSelect}
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a packing list" />
            </SelectTrigger>
            <SelectContent>
              {isLoading ? (
                <div className="flex justify-center p-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : packingLists?.length === 0 ? (
                <div className="text-center p-2 text-sm text-muted-foreground">
                  No packing lists available
                </div>
              ) : (
                packingLists?.map(list => (
                  <SelectItem key={list.id} value={list.id}>
                    {list.title} {list.destination_type ? `(${list.destination_type})` : ''}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        
        <Button onClick={handleCreateNewList} type="button">
          <Plus className="h-4 w-4 mr-1" /> New
        </Button>
      </div>
      
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Packing List</DialogTitle>
          </DialogHeader>
          <PackingListEditor 
            blogPostId={blogPostId} 
            onSave={handleListCreated}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
